import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Calendar, Clock, ArrowLeft, ArrowRight, Share2, Tag, ChevronRight, Loader2 } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import BlogSEO from '../../components/BlogSEO';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticle = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/articles/${slug}`);
        if (res.ok) {
          const data = await res.json();
          // Transform to expected format
          setArticle({
            ...data,
            titleEn: data.title_en || data.title,
            excerptEn: data.excerpt_en || data.excerpt,
            contentEn: data.content_en || data.content,
            categoryNameEn: data.category_name_en || data.category_name,
            metaDescriptionEn: data.meta_description_en || data.meta_description,
            featuredImage: data.featured_image,
            readingTime: data.reading_time,
            publishedAt: data.published_at || data.created_at
          });
          
          // Fetch related articles (same category)
          const relatedRes = await fetch(`${API_URL}/api/blog/articles?status=published`);
          if (relatedRes.ok) {
            const allArticles = await relatedRes.json();
            const related = allArticles
              .filter(a => a.slug !== slug && a.category === data.category)
              .slice(0, 3)
              .map(a => ({
                ...a,
                titleEn: a.title_en || a.title,
                featuredImage: a.featured_image,
                readingTime: a.reading_time
              }));
            setRelatedArticles(related);
          }
        }
      } catch (err) {
        console.error('Failed to fetch article:', err);
      }
      setLoading(false);
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: language === 'de' ? article.title : article.titleEn,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(language === 'de' ? 'Link kopiert!' : 'Link copied!');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
      </div>
    );
  }

  if (!article) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className="text-center">
          <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Artikel nicht gefunden' : 'Article not found'}
          </h1>
          <Link to="/blog" className="text-[#7BA7C2] hover:underline">
            {language === 'de' ? 'Zurück zum Blog' : 'Back to Blog'}
          </Link>
        </div>
      </div>
    );
  }

  const content = language === 'de' ? article.content : article.contentEn;
  const title = language === 'de' ? article.title : article.titleEn;
  const categoryName = language === 'de' ? article.categoryName : article.categoryNameEn;
  const faq = article.faq || [];

  return (
    <>
      <BlogSEO type="article" article={article} />
      <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <Header />
        
        <main className="pt-32 pb-20">
          <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className={`hover:text-[#7BA7C2] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Home
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <Link to="/blog" className={`hover:text-[#7BA7C2] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Blog
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className={isDark ? 'text-white' : 'text-gray-900'}>{categoryName}</span>
            </nav>

            {/* Article Header */}
            <header className="mb-10">
              <Link
                to={`/blog/kategorie/${article.category}`}
                className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4 ${
                  isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#7BA7C2]/10 text-[#5A8BA6]'
                }`}
              >
                {categoryName}
              </Link>
              
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {title}
              </h1>

              <div className={`flex flex-wrap items-center gap-4 mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.publishedAt)}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {article.readingTime} min {language === 'de' ? 'Lesezeit' : 'read'}
                </span>
                <button 
                  onClick={handleShare}
                  className="flex items-center gap-2 hover:text-[#7BA7C2] transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  {language === 'de' ? 'Teilen' : 'Share'}
                </button>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img 
                src={article.featuredImage} 
                alt={title}
                className="w-full h-auto"
              />
            </div>

            {/* Article Content */}
            <div className="blog-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({node, ...props}) => (
                    <h2 className={`text-2xl sm:text-3xl font-bold mt-12 mb-6 pb-3 border-b ${
                      isDark ? 'text-white border-gray-700' : 'text-gray-900 border-gray-200'
                    }`} {...props} />
                  ),
                  h3: ({node, ...props}) => (
                    <h3 className={`text-xl sm:text-2xl font-semibold mt-8 mb-4 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`} {...props} />
                  ),
                  h4: ({node, ...props}) => (
                    <h4 className={`text-lg font-semibold mt-6 mb-3 ${
                      isDark ? 'text-gray-200' : 'text-gray-800'
                    }`} {...props} />
                  ),
                  p: ({node, ...props}) => (
                    <p className={`text-base leading-relaxed mb-5 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`} {...props} />
                  ),
                  ul: ({node, ...props}) => (
                    <ul className={`list-disc list-outside ml-6 mb-6 space-y-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`} {...props} />
                  ),
                  ol: ({node, ...props}) => (
                    <ol className={`list-decimal list-outside ml-6 mb-6 space-y-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`} {...props} />
                  ),
                  li: ({node, ...props}) => (
                    <li className="text-base leading-relaxed" {...props} />
                  ),
                  strong: ({node, ...props}) => (
                    <strong className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} {...props} />
                  ),
                  table: ({node, ...props}) => (
                    <div className="overflow-x-auto my-8 rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}">
                      <table className="min-w-full" {...props} />
                    </div>
                  ),
                  thead: ({node, ...props}) => (
                    <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'} {...props} />
                  ),
                  th: ({node, ...props}) => (
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-white border-b border-gray-700' : 'text-gray-900 border-b border-gray-200'
                    }`} {...props} />
                  ),
                  td: ({node, ...props}) => (
                    <td className={`px-4 py-3 text-sm ${
                      isDark ? 'text-gray-300 border-b border-gray-700' : 'text-gray-600 border-b border-gray-100'
                    }`} {...props} />
                  ),
                  blockquote: ({node, ...props}) => (
                    <blockquote className={`border-l-4 pl-6 py-4 my-8 rounded-r-lg ${
                      isDark 
                        ? 'border-[#7BA7C2] bg-[#7BA7C2]/10 text-gray-300' 
                        : 'border-[#5A8BA6] bg-[#5A8BA6]/5 text-gray-700'
                    }`} {...props} />
                  ),
                  a: ({node, href, ...props}) => {
                    const linkClass = `font-medium underline decoration-2 underline-offset-2 transition-colors ${
                      isDark ? 'text-[#7BA7C2] hover:text-[#9BBFD9]' : 'text-[#5A8BA6] hover:text-[#7BA7C2]'
                    }`;
                    if (href?.startsWith('/')) {
                      return <Link to={href} className={linkClass} {...props} />;
                    }
                    return <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass} {...props} />;
                  },
                  hr: ({node, ...props}) => (
                    <hr className={`my-10 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} {...props} />
                  ),
                  code: ({node, inline, ...props}) => (
                    inline 
                      ? <code className={`px-1.5 py-0.5 rounded text-sm font-mono ${
                          isDark ? 'bg-gray-800 text-[#7BA7C2]' : 'bg-gray-100 text-[#5A8BA6]'
                        }`} {...props} />
                      : <code className="block" {...props} />
                  ),
                  pre: ({node, ...props}) => (
                    <pre className={`p-4 rounded-xl my-6 overflow-x-auto text-sm ${
                      isDark ? 'bg-gray-800' : 'bg-gray-100'
                    }`} {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>

            {/* FAQ Section */}
            {faq.length > 0 && (
              <section className="mt-12 pt-12 border-t border-gray-200 dark:border-gray-800">
                <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'de' ? 'Häufig gestellte Fragen' : 'Frequently Asked Questions'}
                </h2>
                <div className="space-y-4">
                  {faq.map((item, index) => (
                    <details 
                      key={index}
                      className={`group rounded-lg ${
                        isDark ? 'bg-white/5' : 'bg-white shadow-sm'
                      }`}
                    >
                      <summary className={`flex items-center justify-between cursor-pointer p-4 font-medium ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {language === 'de' ? item.question : item.questionEn}
                        <ChevronRight className="w-5 h-5 transition-transform group-open:rotate-90" />
                      </summary>
                      <div className={`px-4 pb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {language === 'de' ? item.answer : item.answerEn}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* Tags */}
            <div className="mt-10 pt-10 border-t border-gray-200 dark:border-gray-800">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog/tag/${tag}`}
                    className={`px-3 py-1 rounded-full text-sm ${
                      isDark 
                        ? 'bg-white/10 text-gray-300 hover:bg-[#7BA7C2]/30' 
                        : 'bg-gray-100 text-gray-600 hover:bg-[#7BA7C2]/20'
                    } transition-colors`}
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Back to Blog */}
            <div className="mt-10">
              <Link
                to="/blog"
                className={`inline-flex items-center gap-2 ${
                  isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                } hover:underline`}
              >
                <ArrowLeft className="w-4 h-4" />
                {language === 'de' ? 'Zurück zum Blog' : 'Back to Blog'}
              </Link>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
              <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {language === 'de' ? 'Ähnliche Artikel' : 'Related Articles'}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/blog/${related.slug}`}
                    className={`group rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl ${
                      isDark ? 'bg-white/5' : 'bg-white shadow-md'
                    }`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={related.featuredImage} 
                        alt={language === 'de' ? related.title : related.titleEn}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className={`font-bold mb-2 group-hover:text-[#7BA7C2] transition-colors ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {language === 'de' ? related.title : related.titleEn}
                      </h3>
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {related.readingTime} min
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
            <div className={`text-center p-10 rounded-2xl ${
              isDark ? 'bg-gradient-to-r from-[#7BA7C2]/20 to-[#5A8BA6]/20' : 'bg-gradient-to-r from-[#7BA7C2]/10 to-[#5A8BA6]/10'
            }`}>
              <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {language === 'de' ? 'Bereit für den nächsten Schritt?' : 'Ready for the Next Step?'}
              </h2>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'de' 
                  ? 'Vereinbaren Sie jetzt Ihre kostenlose Beratung.'
                  : 'Schedule your free consultation now.'}
              </p>
              <Link
                to="/#kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#7BA7C2] text-white rounded-full font-medium hover:bg-[#5A8BA6] transition-colors"
              >
                {language === 'de' ? 'Kostenlose Beratung' : 'Free Consultation'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticle;
